import { githubTestRunPath } from '../lib/githubTestRun'
import { useState } from 'react'
import { Link } from './nav'
import { Clock, Tag, Folder, ExternalLink, Webhook, Route, Users, Bug as BugIcon, Sparkles, GitPullRequest } from 'lucide-react'
import type { Bug } from '../data/projectTypes'
import SourceLinkCard, { passTypeLabel } from './SourceLinkCard'
import BugMetadataSelectors from './BugMetadataSelectors'
import { isWebhookBlocked } from '../../netlify/functions/lib/bug-statuses'
import ReplayLink from './ReplayLink'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { useSendBugToWebhookMutation } from '../services/projectDataApi'
import { useAdminMode } from '../hooks/useAdminMode'
import PullRequestCheckStatus from './PullRequestCheckStatus'
import './BugMetadata.css'

interface BugMetadataProps {
  bug: Bug
  hasWebhook?: boolean
}

export default function BugMetadata({ bug, hasWebhook }: BugMetadataProps) {
  const [sendBugToWebhook] = useSendBugToWebhookMutation()
  const fullDate = new Date(bug.discovered_at).toLocaleString()
  const relativeTime = getRelativeTime(bug.discovered_at)
  const [sending, setSending] = useState(false)
  // Links into app.replay.io are an internal debugging aid; only admins see the recording rows.
  const adminMode = useAdminMode()

  return (
    <section className="bug-metadata" data-testid="bug-metadata" aria-labelledby="details-heading">
      <h2 id="details-heading">Details</h2>

      <div className="bug-metadata__status-row" data-testid="status-manager">
        <BugMetadataSelectors bug={bug} />
      </div>

      <dl className="bug-metadata__list">
        <div className="bug-metadata__item" data-testid="metadata-discovered">
          <Clock size={14} className="bug-metadata__icon" aria-hidden="true" />
          <dt className="bug-metadata__label">Discovered</dt>
          <dd className="bug-metadata__value" title={fullDate} data-testid="discovered-timestamp">
            {relativeTime}
          </dd>
        </div>

        {/* Type: a general "Testing" bug (from a QA/test run) vs a "Polish" bug from a specific
            polish pass (the pass type is shown so the reader knows which polish surfaced it). */}
        <div className="bug-metadata__item" data-testid="metadata-type">
          {bug.polish_category
            ? <Sparkles size={14} className="bug-metadata__icon" aria-hidden="true" />
            : <BugIcon size={14} className="bug-metadata__icon" aria-hidden="true" />}
          <dt className="bug-metadata__label">Type</dt>
          <dd className="bug-metadata__value" data-testid="bug-type">
            {bug.polish_category ? `Polish · ${passTypeLabel(bug.polish_category)}` : 'Testing'}
          </dd>
        </div>

        <div className="bug-metadata__item" data-testid="metadata-test-run">
          <Tag size={14} className="bug-metadata__icon" aria-hidden="true" />
          <dt className="bug-metadata__label">Source</dt>
          {bug.test_run_id ? (
            <dd className="bug-metadata__value" data-testid="test-run-link">
              <SourceLinkCard
                sourceType="test_run"
                sourceId={bug.test_run_id}
                projectId={bug.project_id}
                sourceMeta={{
                  journey_name: bug.journey_name ?? undefined,
                  journey_id: bug.journey_id ?? undefined,
                  project_name: bug.project_name,
                }}
                link={taskDetailUrl(bug.project_id, bug.test_run_id)}
              />
            </dd>
          ) : bug.task_id ? (
            <dd className="bug-metadata__value" data-testid="task-link">
              <SourceLinkCard
                sourceType="task"
                sourceId={bug.task_id}
                projectId={bug.project_id}
                sourceMeta={{ project_name: bug.project_name }}
                link={taskDetailUrl(bug.project_id, bug.task_id)}
              />
            </dd>
          ) : (
            <dd className="bug-metadata__value bug-metadata__value--muted" data-testid="test-run-manual">Manual entry</dd>
          )}
        </div>

        {bug.journey_id && (
          <div className="bug-metadata__item" data-testid="metadata-journey">
            <Route size={14} className="bug-metadata__icon" aria-hidden="true" />
            <dt className="bug-metadata__label">Journey</dt>
            <dd className="bug-metadata__value">
              <Link
                to={`/projects/${bug.project_id}/journeys/${bug.journey_id}`}
                className="bug-metadata__link"
                data-testid="journey-link"
                data-analytics-event="bug-journey-link"
              >
                {bug.journey_name ?? bug.journey_id}
              </Link>
            </dd>
          </div>
        )}

        {bug.fix_prs?.map(pr => (
          <div className="bug-metadata__item" key={`${pr.repo_full_name}#${pr.pr_number}`}>
            <GitPullRequest size={14} className="bug-metadata__icon" aria-hidden="true" />
            <dt className="bug-metadata__label">Fix PR</dt>
            <dd className="bug-metadata__value">
              <a href={`https://github.com/${pr.repo_full_name}/pull/${pr.pr_number}`} target="_blank" rel="noreferrer" className="bug-metadata__link">
                # {pr.pr_number}
                <PullRequestCheckStatus projectId={bug.project_id} repo={pr.repo_full_name} pr={pr.pr_number} />
              </a>
              <span> · {pr.merged_at ? 'Merged' : !pr.active ? 'Unlinked' : pr.pr_state === 'closed' ? 'Closed without merge' : pr.pr_state === 'merged' ? 'Merged into another branch' : 'Fix proposed'}</span>
              {pr.verification_detail && <p>{pr.verification_detail}</p>}
              {pr.version_id && <Link to={githubTestRunPath(bug.project_id, pr.version_id)} className="bug-metadata__link"> View retest</Link>}
            </dd>
          </div>
        ))}

        {bug.pr_number && bug.pr_repo_full_name && (
          <div className="bug-metadata__item" data-testid="metadata-pull-request">
            <GitPullRequest size={14} className="bug-metadata__icon" aria-hidden="true" />
            <dt className="bug-metadata__label">Introduction PR</dt>
            <dd className="bug-metadata__value">
              <a
                href={`https://github.com/${bug.pr_repo_full_name}/pull/${bug.pr_number}`}
                className="bug-metadata__link"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bug-pr-link"
                data-analytics-event="bug-pr-link"
              >
                # {bug.pr_number}
                <PullRequestCheckStatus projectId={bug.project_id} repo={bug.pr_repo_full_name} pr={bug.pr_number} />
                {bug.version_branch ? <span className="bug-metadata__value--muted"> · {bug.version_branch}</span> : null}
              </a>
            </dd>
          </div>
        )}

        <div className="bug-metadata__item" data-testid="metadata-project">
          <Folder size={14} className="bug-metadata__icon" aria-hidden="true" />
          <dt className="bug-metadata__label">Project</dt>
          <dd className="bug-metadata__value">
            <Link
              to={`/projects/${bug.project_id}/overview`}
              className="bug-metadata__link"
              data-testid="project-link"
              data-analytics-event="bug-project-link"
            >
              {bug.project_name ?? bug.project_id}
            </Link>
          </dd>
        </div>

        {adminMode && (() => {
          let parsedIds: Record<string, string> | null = bug.test_run_replay_recording_ids
          if (typeof parsedIds === 'string') {
            try { parsedIds = JSON.parse(parsedIds) } catch { parsedIds = null }
          }
          const hasMultiple = parsedIds && typeof parsedIds === 'object' && Object.keys(parsedIds).length > 0
          if (hasMultiple) {
            const entries = Object.entries(parsedIds!)
            if (bug.replay_recording_id && !entries.some(([, v]) => v === bug.replay_recording_id)) {
              entries.push(['recording', bug.replay_recording_id])
            }
            return (
              <div className="bug-metadata__item bug-metadata__item--recordings" data-testid="metadata-replay">
                <ExternalLink size={14} className="bug-metadata__icon" aria-hidden="true" />
                <dt className="bug-metadata__label">Recordings</dt>
                <dd className="bug-metadata__value">
                  <div className="bug-metadata__recordings-list">
                    {entries.map(([ctx, recId]) => (
                      <div key={ctx} className="bug-metadata__recording-item" data-testid={`recording-${ctx}`}>
                        <span className="bug-metadata__recording-context">
                          <Users size={10} aria-hidden="true" /> {ctx}
                        </span>
                        <ReplayLink replayRecordingId={recId} />
                      </div>
                    ))}
                  </div>
                </dd>
              </div>
            )
          }
          return (
            <div className="bug-metadata__item" data-testid="metadata-replay">
              <ExternalLink size={14} className="bug-metadata__icon" aria-hidden="true" />
              <dt className="bug-metadata__label">Replay</dt>
              <dd className="bug-metadata__value">
                {bug.replay_recording_id ? (
                  <ReplayLink replayRecordingId={bug.replay_recording_id} />
                ) : (
                  <span className="bug-metadata__value--muted" data-testid="replay-unavailable">No recording available</span>
                )}
              </dd>
            </div>
          )
        })()}

        <div className="bug-metadata__item" data-testid="metadata-webhook">
          <Webhook size={14} className="bug-metadata__icon" aria-hidden="true" />
          <dt className="bug-metadata__label">Webhook</dt>
          <dd className="bug-metadata__value" data-testid="webhook-status">
            {bug.sent_to_webhook ? 'Sent' : hasWebhook ? (
              isWebhookBlocked(bug.status) ? 'Not Sent' : (
              <button
                type="button"
                className="bug-metadata__send-webhook-btn"
                disabled={sending}
                onClick={() => {
                  setSending(true)
                  void sendBugToWebhook(bug.id).finally(() => setSending(false))
                }}
                data-testid="send-webhook-btn"
                data-analytics-event="bug-webhook-send-button"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            )) : 'Not sent'}
          </dd>
        </div>
      </dl>
    </section>
  )
}

function getRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((todayStart.getTime() - dateStart.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return `Today at ${time}`
  if (diffDays === 1) return `Yesterday at ${time}`
  if (diffDays < 30) return `${diffDays} days ago at ${time}`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return `1 month ago at ${time}`
  return `${diffMonths} months ago at ${time}`
}
